// visual_check — mesurer l'app avant d'enregistrer une retouche, et refuser si ça casse.
//
// ── CE QUE ÇA TIENT ─────────────────────────────────────────────────────────
//
// Sa contrainte, le 2026-10-05 : « on veut aussi que ca ne nique pas la responsivité ». Les
// gestes ne produisent que des pourcentages et un ratio, donc ils ne peuvent pas figer un
// pixel — mais une photo à 100 % dans un conteneur qui a déjà des marges sort quand même de
// l'écran. La promesse ne vaut donc que mesurée.
//
// ── OÙ ÇA MESURE, ET POURQUOI PAS SUR LE NŒUD ───────────────────────────────
//
// Dans un cadre CACHÉ de la console, en arrière-plan. Trois raisons, dans cet ordre :
//
//   • L'APERÇU VISIBLE NE DOIT PAS SAUTER. Mesurer trois largeurs avant et après, dans le cadre
//     que l'opérateur regarde, le ferait se tordre pendant une seconde à chaque retouche. « Do
//     not do shitty ui » : un cadre caché coûte un chargement de plus et rien de visible.
//   • ÇA ARRIVE AVANT L'ENREGISTREMENT. Mesurer sur le nœud demanderait un déploiement par
//     retouche, pour une fonction faite de « petits détails » — et le refus tomberait après coup.
//   • LA MESURE EST GRATUITE ICI : la console sait déjà encadrer l'app, et l'app sait déjà se
//     mesurer (_runtime/visual_measure) et répondre (_runtime/visual_bridge).
//
// Ce n'est donc PAS une frontière de sûreté : un POST fabriqué à la main peut encore enregistrer
// une valeur qui casse. Mais elle ne casse que la mise en page de l'app de celui qui l'envoie, et
// c'est son app. Si ça doit un jour être tenu côté serveur, visual_verdict est pur et s'importe
// tel quel.
//
// ── ET LE MESUREUR EST INJECTÉ ──────────────────────────────────────────────
//
// Tout ce qui touche à l'iframe est derrière une fonction. C'est ce qui permet d'éprouver
// l'orchestration sans navigateur, y compris les cas qu'un navigateur rend pénibles à
// provoquer : une app muette, une app qui répond pour la mauvaise largeur.
import { type Verdict, type WidthMeasure, verdictFor } from "./visual_verdict";

/**
 * Appliquer un document et une largeur au cadre de mesure, demander une mesure, et rendre la
 * réponse — ou `null` si l'app n'a pas répondu.
 *
 * `id` doit revenir TEL QUEL dans la réponse : c'est ce qui empêche la réponse tardive d'une
 * largeur d'être lue comme la mesure de la suivante.
 */
export type Mesurer = (doc: unknown, width: number, id: number) => Promise<WidthMeasure | null>;

/**
 * Les largeurs vérifiées.
 *
 * TROIS, ET PAS SEPT : sept feraient quatorze mesures par retouche, et une garde trop lourde
 * finit par être rendue optionnelle. Ce sont trois des sept largeurs que
 * `scripts/viewport_audit.ts` mesure déjà — vérifier une largeur que rien d'autre ne vérifie
 * donnerait une confiance qu'on n'a pas.
 *
 * 360 D'ABORD, et c'est la plus étroite de l'audit : c'est là que la place manque, donc là
 * qu'une photo trop large se voit d'abord. L'aperçu, lui, montre 390 — un téléphone courant.
 * Prévisualiser et garder ne sont pas le même travail : l'un montre le cas habituel, l'autre
 * cherche le pire.
 */
export const CHECK_WIDTHS: readonly number[] = [360, 768, 1280];

/** Mesurer un document à chaque largeur. Les largeurs muettes sont simplement absentes. */
async function mesurerTout(
  mesurer: Mesurer,
  doc: unknown,
  id: () => number,
): Promise<WidthMeasure[]> {
  const out: WidthMeasure[] = [];
  for (const width of CHECK_WIDTHS) {
    const m = await mesurer(doc, width, id());
    if (m === null) continue;
    // L'APP RAPPORTE SA PROPRE LARGEUR, et si elle ne correspond pas à celle qu'on a demandée,
    // le cadre n'avait pas fini de se redimensionner. Comparer deux largeurs différentes dirait
    // n'importe quoi, alors on écarte la mesure plutôt que de la croire.
    if (m.width !== width) continue;
    out.push(m);
  }
  return out;
}

/**
 * La retouche est-elle enregistrable ?
 *
 * ON MESURE L'AVANT EN PREMIER, et pas par goût : à la fin, le cadre de mesure porte le
 * document CANDIDAT. L'inverse le laisserait sur l'ancien, et la vérification suivante
 * partirait d'un état qui ne correspond à rien.
 *
 * ON N'ACCUSE JAMAIS SUR UNE ABSENCE DE PREUVE. Une app muette — toute app déployée avant cette
 * version en est une — donne zéro mesure, donc rien à comparer, donc « acceptable ». Refuser le
 * geste de l'opérateur parce que son app est ancienne le punirait d'un problème qui n'est pas le
 * sien. C'est le même repli que visual_verdict applique déjà, et il est délibéré.
 */
export async function verifierChangement(
  mesurer: Mesurer,
  base: unknown,
  candidat: unknown,
): Promise<Verdict> {
  let n = 0;
  const id = () => ++n;
  const avant = await mesurerTout(mesurer, base, id);
  const apres = await mesurerTout(mesurer, candidat, id);
  return verdictFor(avant, apres);
}
